/**
 * RegisterPage
 * Compact, space-efficient auth page design
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { RegisterForm } from "../components/auth/RegisterForm";
import { ROUTES } from "../services/routes";
import { ArrowLeft, Sparkles, Check } from "lucide-react";

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();

  const benefits = [
    "20+ AI analysis services",
    "Real-time processing",
    "Enterprise security",
    "Exportable reports",
  ];

  return (
    <div className="h-screen overflow-hidden bg-navy-900 flex">
      {/* Left Panel - Branding (40% width) */}
      <div className="hidden lg:flex lg:w-[40%] h-screen relative overflow-hidden">
        {/* Content */}
        <div className="relative z-10 flex flex-col h-full w-full p-8 xl:p-10">
          {/* Logo */}
          <Link
            to={ROUTES.HOME}
            className="flex items-center gap-2 flex-shrink-0"
          >
            <img
              src="/logo.png"
              alt="UniVision"
              className="w-10 h-10 rounded-md object-contain"
            />
            <div className="text-xl font-bold text-white">
              Uni
              <span className="text-cyan-400">Vision</span>
            </div>
          </Link>

          {/* Main Content */}
          <div className="flex-1 flex flex-col justify-center max-w-sm">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500/10 to-accent-500/10 border border-primary-500/20 rounded-full px-3 py-1.5 mb-4 w-fit">
              <Sparkles className="w-3 h-3 text-primary-400" />
              <span className="text-xs font-medium text-primary-300">
                Get Started Free
              </span>
            </div>

            <h1 className="text-3xl xl:text-4xl font-bold text-white mb-3 leading-tight">
              Start Your
              <span className="block bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                AI Journey
              </span>
            </h1>
            <p className="text-sm text-slate-400 mb-6">
              Create your free account and unlock computer vision analytics.
            </p>

            {/* Benefits - Compact */}
            <div className="space-y-2">
              {benefits.map((benefit, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 text-slate-400 text-sm"
                >
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 flex items-center justify-between text-sm">
            <Link
              to={ROUTES.HOME}
              className="inline-flex items-center gap-1.5 text-slate-400 hover:text-primary-400 transition-colors"
            >
              <ArrowLeft className="w-3 h-3" />
              Home
            </Link>
            <span className="text-slate-600">
              © {new Date().getFullYear()} UniVision
            </span>
          </div>
        </div>
      </div>

      {/* Right Panel - Form (60% width) */}
      <div className="w-full lg:w-[60%] h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8 bg-slate-900/50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-4 text-center">
            <Link to={ROUTES.HOME} className="inline-flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center overflow-hidden">
                <img
                  src="/logo.png"
                  alt="UniVision"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <span className="text-lg font-bold text-white">
                UniVision
              </span>
            </Link>
          </div>

          {/* Glass Card */}
          <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-5 sm:p-6 shadow-2xl">
            <RegisterForm onSwitchToLogin={() => navigate(ROUTES.LOGIN)} />
          </div>

          {/* Mobile Back to Home */}
          <div className="lg:hidden mt-3 text-center">
            <Link
              to={ROUTES.HOME}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-primary-400 transition-colors"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
